import React from 'react'
import { PPSServer } from 'lib/pps'
import { Id } from 'types/Id'
import ChunkerButtonWithLoading from 'components/ChunkerButtonWithLoading'
import useNotify from 'hooks/useNotify'
import { Box, Tooltip, Typography } from '@mui/material'

interface DownloadContactedCsvButtonProps
  extends React.ComponentProps<typeof ChunkerButtonWithLoading> {
  opportunityId: Id
  opportunityName: string
  disabled: boolean
}

export default function DownloadSolutionContactedCSVButton({
  opportunityId,
  opportunityName,
  disabled,
  ...props
}: DownloadContactedCsvButtonProps) {
  const { notifySuccess, notifyError } = useNotify()
  const [downloading, setDownloading] = React.useState(false)
  const opportunityNameFormatted = opportunityName.replace(/\s+/g, '_')
  const filename = `Opportunity_Solution_Contacts_${opportunityNameFormatted}_Opportunity_${opportunityId}.csv`

  const handleDownload = async () => {
    // Use the PPSServer class method to fetch the CSV blob
    const blob = await PPSServer.downloadOpportunitySolutionContactedCsv(opportunityId)

    // Trigger browser download
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Tooltip
      title={
        disabled ? (
          <Box sx={{ p: 1, borderRadius: 1 }}>
            <Typography variant="body1" color="primary.contrastText">
              Contacts may only be downloaded when <strong>STATUS: COMPLETE</strong> is present
              within the campaign information.
            </Typography>
            <Typography variant="body1" color="primary.contrastText" mt={2}>
              Please check back when the campaign has been completed.
            </Typography>
            <Typography variant="body1" color="primary.contrastText" mt={2}>
              NOTE: If a campaign did not successfully complete, there are no contacts to be
              download.
            </Typography>
          </Box>
        ) : (
          ''
        )
      }
      placement="top"
      arrow
      disableInteractive
    >
      <span>
        <ChunkerButtonWithLoading
          disabled={disabled}
          disabledLoading={downloading}
          onClick={() => {
            ;(async () => {
              try {
                setDownloading(true)
                await handleDownload()
                notifySuccess(`Successfully downloaded CSV: ${filename}`)
              } catch (error) {
                console.error('Error downloading CSV:', error)
                notifyError('Failed to download CSV')
              } finally {
                setDownloading(false)
              }
            })().catch(console.error)
          }}
          {...props}
        >
          Download CSV
        </ChunkerButtonWithLoading>
      </span>
    </Tooltip>
  )
}
